import isEmpty from 'lodash/isEmpty';
import { getBillEstimateV2 } from '@atlassian/cssxp-proxies';
import { BillEstimateOrderItemDto } from '@atlassian/cssxp-ccp-types';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { getDiscretePromotionId, updateAdjustmentsToPartnerDiscounts } from '@css-xp/ppc-utils';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { CartLinkedEntitlementsSchema, CartProductSchema, PromotionTypes } from '../../types';
import type { State } from '../types';
import { OmsCapability, getOmsPayload, CustomOrderEntitlement } from '.';
import { getAdjustedDateForMonthEstimate } from '../../utils';

type BillEstimate = {
  estimate: BillEstimateOrderItemDto;
  linkedEstimates: BillEstimateOrderItemDto[];
};

export enum BillEstimateType {
  RECURRING_ESTIMATE = 'RECURRING_ESTIMATE',
  NEXT_ESTIMATE = 'NEXT_ESTIMATE',
}

/**
 * Generate promotion payload as discretionary type for phased promotions
 * This payload will be generated based on which iteration the subscription is part of the promotion
 * https://hello.atlassian.net/wiki/spaces/~686306312/pages/3837344420/Solution+Approach+Bill+Estimation+for+current+cycle+of+entitlement
 * @param percentage
 */
export const getDiscretionaryPromotionPayload = (percentage: number) => {
  const promotionId = getDiscretePromotionId();
  return {
    promotionInstanceId: null,
    promotionDefinition: {
      promotionId: promotionId,
      customisedValues: {
        applicationReason: {
          id: 'other',
        },
        benefits: [
          {
            value: percentage,
            iterations: undefined,
            duration: PromotionDuration.ONCE,
          },
        ],
      },
    },
  };
};
/**
 * This method created the phased promotion for logged in partner based on benefitStates of the current promotions on subscription
 * If existing partner promotions are not there, it will return the current partner promotion payload
 * If existing partner promotion is there, it will map with the billing date and fine the current phase of promotion and map with current
 * partner promotion benefits. e.g. a subscription on Gold partner can have 20% promotion for 14 month while platinum can have 25% for same month,
 * while transferring we will send the 25% as promotion for bill estimations.
 * @param lineItem
 * @param lineItemPromotionKey
 * @param cartProducts
 * @param cartApps
 * @param partnerPromotionBenefits
 * @param partnerPromotions
 */
export const getPhasedPromotionPayload = ({
  lineItem,
  // different line items can have different keys for promotion
  lineItemPromotionKey = 'addPromotions',
  cartProducts,
  cartApps,
  partnerPromotionBenefits,
  partnerPromotions,
}: {
  lineItem: CustomOrderEntitlement;
  lineItemPromotionKey?: string;
  cartProducts: CartProductSchema[];
  cartApps: CartLinkedEntitlementsSchema[];
  partnerPromotionBenefits: State['partnerPromotionBenefits'];
  partnerPromotions: State['partnerPromotions'];
}) => {
  // @ts-ignore
  const addPromotionsArr = lineItem?.[lineItemPromotionKey] as CustomOrderEntitlement['addPromotions'];
  const cartEntitlement =
    cartProducts.find((product) => product.id === lineItem.entitlementId || product.id === lineItem.itemId) ||
    cartApps.find((app) => app.id === lineItem.entitlementId || app.id === lineItem.itemId);

  if (!addPromotionsArr?.length || !cartEntitlement) {
    return addPromotionsArr;
  }
  const lineItemPartnerPromotionIndex = addPromotionsArr.findIndex(
    (promotion) =>
      promotion.promotionDefinition?.promotionId &&
      !!partnerPromotionBenefits[promotion.promotionDefinition.promotionId]
  );
  const currentPartnerPromotion = cartEntitlement.promotions?.find(
    (promotion) => promotion.type === PromotionTypes.PARTNER_DISCOUNT
  );
  if (
    typeof lineItemPartnerPromotionIndex === undefined ||
    !currentPartnerPromotion ||
    !currentPartnerPromotion.benefitStates
  ) {
    return addPromotionsArr;
  }

  // first check based on benefit end date, the index of the status
  const indexByNextBillingDate = currentPartnerPromotion.benefitStates?.findIndex(
    (state) => state?.benefitEnd && state?.benefitEnd >= Number(cartEntitlement.nextBillDate)
  );
  // in case index is not found, set default value as 2
  const existingStateIndex = indexByNextBillingDate >= 0 ? indexByNextBillingDate : 2;

  const currentPartnerPromotionId = partnerPromotions[cartEntitlement.productId];
  // getting the current partner benefits data and using to calculate the updated discount percentage
  const currentPartnerBenefit =
    currentPartnerPromotionId &&
    partnerPromotionBenefits?.[currentPartnerPromotionId] &&
    partnerPromotionBenefits[currentPartnerPromotionId][existingStateIndex];
  if (!currentPartnerBenefit) {
    return addPromotionsArr;
  }
  addPromotionsArr.splice(lineItemPartnerPromotionIndex, 1);
  const discretionaryPromotionPayload = getDiscretionaryPromotionPayload(currentPartnerBenefit.value as number);
  addPromotionsArr.push(discretionaryPromotionPayload);
  return addPromotionsArr;
};

export async function getBillEstimate({
  cartProducts,
  cartApps,
  nextBillDate,
  selectedProducts,
  transactionAccountId,
  partnerPromotions,
  invoiceGroupId = '',
  estimateType,
  products,
  selectedLinkedEntitlements,
  offeringsAndpricingPlans,
  estimatedUsersForRecurringBillEstimate,
  shouldExcludeDiscretionaryDiscount = false,
  partnerPromotionBenefits,
  currentPricingEligibility,
  includeSubscriptionStartTime = false,
  currentCurrency,
  isNonCommercialPricingEnabled = false,
  isExcludeExpiredPromotionEnabled = false,
  isAppEditionSupportEnabled = false,
}: {
  cartProducts: CartProductSchema[];
  cartApps: State['linkedEntitlements'];
  nextBillDate: State['nextBillDate'];
  selectedProducts: State['selectedProducts'];
  transactionAccountId: string;
  partnerPromotions: State['partnerPromotions'];
  invoiceGroupId: string;
  estimateType: BillEstimateType;
  products: State['products'];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  estimatedUsersForRecurringBillEstimate: State['estimatedUsersForRecurringBillEstimate'];
  shouldExcludeDiscretionaryDiscount?: boolean;
  partnerPromotionBenefits: State['partnerPromotionBenefits'];
  currentPricingEligibility?: CCPPricing;
  includeSubscriptionStartTime?: boolean;
  currentCurrency: `${Currency}`;
  isNonCommercialPricingEnabled?: boolean;
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}) {
  const payload = await getOmsPayload({
    cartProducts,
    nextBillDate,
    selectedProducts,
    transactionAccountId,
    partnerPromotions,
    cartApps,
    invoiceGroupId,
    omsCapability: OmsCapability.BILL_ESTIMATE,
    productsArr: Object.values(products),
    offeringsAndpricingPlans,
    selectedLinkedEntitlements,
    shouldExcludeDiscretionaryDiscount,
    currentPricingEligibility,
    sandboxProducts: {},
    sandboxApps: {},
    currentCurrency,
    isNonCommercialPricingEnabled,
    initialStoreProducts: products,
    isExcludeExpiredPromotionEnabled,
    isAppEditionSupportEnabled,
  });

  const { isLastThreeDaysOfMonth, firstDayOfNextMonthTimeStamp } = getAdjustedDateForMonthEstimate();

  const isRecurringEstimate = estimateType === BillEstimateType.RECURRING_ESTIMATE;
  payload?.entitlements?.forEach((entitlement: CustomOrderEntitlement) => {
    const entitlementDetails = { ...entitlement };
    const entitlementId = (isRecurringEstimate ? null : entitlementDetails.entitlementId) as string;
    const orderType = isRecurringEstimate ? OrderItemType.CREATION_ORDER : entitlement.orderType;
    entitlement.addPromotions = getPhasedPromotionPayload({
      cartProducts,
      cartApps: Object.values(cartApps).flat(2),
      lineItem: entitlement,
      partnerPromotionBenefits,
      partnerPromotions,
    });
    entitlement.entitlementId = entitlementId;
    entitlement.orderType = orderType;
    entitlement.skipTrial = isRecurringEstimate;

    /**
     * While fetching the estimate on 29th, 30th or 31st of any month, the estimates are generated from today till 28th
     * of the next month. This is happening because that’s the default behavior of
     * CCP - billing anchor date can’t be set for a date greater than 28th.
     *
     * In order to fix this we need to set the subscription start time to the first day of next month only when current date is 29th, 30th or 31st.
     *
     */
    if (includeSubscriptionStartTime && isLastThreeDaysOfMonth) {
      entitlement.subscriptionStartTime = firstDayOfNextMonthTimeStamp;
    }

    // entitlementId is not always present in the entitlement object for apps. It can be itemId as well.
    const entitlementOrItemId = entitlementDetails.entitlementId || entitlementDetails.itemId;
    // if bill estimate is for recurring, we should use the estimated users for recurring bill estimate if it exists
    // this is required to fetch estimated users for recurring in the cart line items not for order placement
    // https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3403322557/Allow+users+column+to+edit+in+UOF+cart+page
    if (isRecurringEstimate && entitlementOrItemId && estimatedUsersForRecurringBillEstimate?.[entitlementOrItemId]) {
      entitlement.chargeQuantity = Number(estimatedUsersForRecurringBillEstimate[entitlementOrItemId]);
    }
  });

  const billEstimate: BillEstimate = await getBillEstimateV2(payload);

  if (typeof billEstimate === 'string' && billEstimate === 'OK') {
    throw new Error('Bill estimate fetch failed');
  }

  updateAdjustmentsToPartnerDiscounts(billEstimate.linkedEstimates);
  /*
    OMS engine was built with AC in mind. In AC we only always deal with a single entitlement.
    This is why we have an `estimate` key in the OMS response.
    Now, we have added logic in OMS to ensure only estimates for entitlements with an `entitlementId` comes inside the `estimate` key in the OMS response.
    As such, for creation only use case, it is possible for the OMS response to have an empty `estimate` key which we need to ignore before processing estimates for unified flow.
  */
  if (isEmpty(billEstimate.estimate)) return { items: billEstimate.linkedEstimates };

  const billEstimateAsReturnedByCcp = {
    items: [billEstimate.estimate, ...billEstimate.linkedEstimates],
  };

  return billEstimateAsReturnedByCcp;
}
